/**
 * Helpers for aging vessel FK normalization and availability filtering.
 * API Aging records use underscored `vessel_id`; the UI form uses `vesselId`.
 */

export type AgingVesselRef = {
  vesselId?: number | null;
  vessel_id?: number | null;
};

export type AgingVesselCandidate = {
  id: number;
  status?: string | null;
  usage_type?: string | null;
  usageType?: string | null;
};

export type AgingProcessVesselStatus = AgingVesselRef & {
  status?: string | null;
};

/** Resolve aging FK whether API returned camelCase or snake_case. */
export function resolveAgingVesselId(
  aging: AgingVesselRef | null | undefined
): number | undefined {
  if (!aging) return undefined;
  const id = aging.vesselId ?? aging.vessel_id;
  if (id === null || id === undefined) return undefined;
  const numeric = Number(id);
  return Number.isFinite(numeric) ? numeric : undefined;
}

/** Normalize an aging record so `vesselId` is always set when `vessel_id` exists. */
export function normalizeAgingVesselId<T extends AgingVesselRef>(aging: T): T & { vesselId: number | undefined } {
  return {
    ...aging,
    vesselId: resolveAgingVesselId(aging),
  };
}

function isAgingCapableVessel(vessel: AgingVesselCandidate): boolean {
  const usageType = vessel.usage_type || vessel.usageType;
  return usageType === 'Aging' || usageType === 'Both';
}

function isFreeVesselStatus(status: string | null | undefined): boolean {
  return status === 'Empty' || status === 'Available';
}

/**
 * Vessels free for starting a new aging process:
 * aging-capable, Empty/Available, and not occupied by another Active aging.
 */
export function getFreeAgingVessels<T extends AgingVesselCandidate>(
  vessels: T[],
  agingProcesses: AgingProcessVesselStatus[]
): T[] {
  const activeVesselIds = new Set(
    agingProcesses
      .filter((a) => a.status === 'Active')
      .map((a) => resolveAgingVesselId(a))
      .filter((id): id is number => id !== undefined)
  );

  return vessels.filter((vessel) => {
    if (!isAgingCapableVessel(vessel)) return false;
    return isFreeVesselStatus(vessel.status) && !activeVesselIds.has(vessel.id);
  });
}

/**
 * Vessels selectable in the aging modal.
 * When editing, always includes the process's current vessel even if In Use.
 */
export function getModalAgingVessels<T extends AgingVesselCandidate>(
  vessels: T[],
  agingProcesses: AgingProcessVesselStatus[],
  editingAging: AgingVesselRef | null | undefined
): T[] {
  const free = getFreeAgingVessels(vessels, agingProcesses);
  const currentVesselId = resolveAgingVesselId(editingAging ?? undefined);
  if (currentVesselId === undefined) {
    return free;
  }

  if (free.some((v) => v.id === currentVesselId)) {
    return free;
  }

  const currentVessel = vessels.find((v) => v.id === currentVesselId);
  if (!currentVessel) {
    return free;
  }

  return [...free, currentVessel];
}
